import { useState, useCallback, useRef, useEffect } from 'react'

export default function useImageZoom(options = {}) {
  const { initialMinScale = 0.1, initialMaxScale = 4, scaleStep = 0.25, containerRef, imageRef } = options

  const [open, setOpen] = useState(false)
  const [scale, setScale] = useState(1)
  const [minScale, setMinScale] = useState(initialMinScale)
  const [maxScale, setMaxScale] = useState(initialMaxScale)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [pinchStartDist, setPinchStartDist] = useState(0)
  const [showControls, setShowControls] = useState(true)
  const controlsTimeout = useRef(null)

  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 })
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 })

  const currentFitScale = useRef(1)

  const calculateFitScale = useCallback(() => {
    if (
      imageDimensions.width > 0 &&
      imageDimensions.height > 0 &&
      containerDimensions.width > 0 &&
      containerDimensions.height > 0
    ) {
      return Math.min(
        containerDimensions.width / imageDimensions.width,
        containerDimensions.height / imageDimensions.height
      )
    }
    return 1
  }, [imageDimensions, containerDimensions])

  useEffect(() => {
    if (open) {
      if (imageRef && imageRef.current) {
        setImageDimensions({
          width: imageRef.current.naturalWidth,
          height: imageRef.current.naturalHeight
        })
      }
      if (containerRef && containerRef.current) {
        setContainerDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight
        })
      }
    }
  }, [open, imageRef, containerRef])

  useEffect(() => {
    if (open && imageDimensions.width > 0 && containerDimensions.width > 0) {
      const fitScaleValue = calculateFitScale()
      currentFitScale.current = fitScaleValue

      setScale(fitScaleValue)
      setMinScale(initialMinScale)
      setMaxScale(Math.max(fitScaleValue, initialMaxScale))
      setPosition({ x: 0, y: 0 })
    }
  }, [open, imageDimensions, containerDimensions, calculateFitScale, initialMinScale, initialMaxScale])

  const handleOpen = useCallback(e => {
    e && e.stopPropagation()
    setOpen(true)
  }, [])

  const handleClose = useCallback(e => {
    e && e.stopPropagation()
    setOpen(false)
  }, [])

  const resetView = useCallback(() => {
    const fitScaleValue = calculateFitScale()
    if (fitScaleValue !== Infinity && !isNaN(fitScaleValue)) {
      currentFitScale.current = fitScaleValue
      setScale(fitScaleValue)
    } else {
      currentFitScale.current = 1
      setScale(1)
    }
    setMinScale(initialMinScale)
    setMaxScale(Math.max(currentFitScale.current, initialMaxScale))
    setPosition({ x: 0, y: 0 })
  }, [calculateFitScale, initialMinScale, initialMaxScale])

  const canDrag = useCallback(() => {
    return scale > currentFitScale.current + 0.001
  }, [scale, currentFitScale])

  const getClampedPosition = useCallback(
    (newX, newY, currentViewScale) => {
      if (!containerDimensions.width || !imageDimensions.width) return { x: 0, y: 0 }

      const scaledImageWidth = imageDimensions.width * currentViewScale
      const scaledImageHeight = imageDimensions.height * currentViewScale

      const effectiveContainerWidth = canDrag() ? containerDimensions.width : scaledImageWidth
      const effectiveContainerHeight = canDrag() ? containerDimensions.height : scaledImageHeight

      const maxOverflowX = Math.max(0, (scaledImageWidth - effectiveContainerWidth) / 2)
      const maxOverflowY = Math.max(0, (scaledImageHeight - effectiveContainerHeight) / 2)

      let clampedX = newX
      let clampedY = newY

      if (scaledImageWidth > containerDimensions.width) {
        clampedX = Math.max(-maxOverflowX, Math.min(maxOverflowX, newX))
      } else {
        clampedX = Math.max(newX, -(scaledImageWidth - containerDimensions.width) / 2)
        clampedX = Math.min(clampedX, (containerDimensions.width - scaledImageWidth) / 2)
      }

      if (scaledImageHeight > containerDimensions.height) {
        clampedY = Math.max(-maxOverflowY, Math.min(maxOverflowY, newY))
      } else {
        clampedY = Math.max(newY, -(scaledImageHeight - containerDimensions.height) / 2)
        clampedY = Math.min(clampedY, (containerDimensions.height - scaledImageHeight) / 2)
      }

      if (scaledImageWidth <= containerDimensions.width)
        clampedX = (containerDimensions.width - scaledImageWidth) / 2 - position.x // Incorrect logic here, should be simpler
      if (scaledImageHeight <= containerDimensions.height)
        clampedY = (containerDimensions.height - scaledImageHeight) / 2 - position.y // Needs rethink for smaller image centering

      const finalClampedX = Math.max(-maxOverflowX, Math.min(maxOverflowX, newX))
      const finalClampedY = Math.max(-maxOverflowY, Math.min(maxOverflowY, newY))

      return { x: finalClampedX, y: finalClampedY }
    },
    [imageDimensions, containerDimensions, canDrag, position.x, position.y]
  )

  const handleZoomIn = useCallback(
    e => {
      e && e.stopPropagation()
      setScale(prevScale => {
        const newScale = Math.min(prevScale + scaleStep, maxScale)
        setPosition(prevPos => getClampedPosition(prevPos.x, prevPos.y, newScale))
        return newScale
      })
    },
    [scaleStep, maxScale, getClampedPosition]
  )

  const handleZoomOut = useCallback(
    e => {
      e && e.stopPropagation()
      setScale(prevScale => {
        const newScale = Math.max(prevScale - scaleStep, minScale)
        setPosition(prevPos => getClampedPosition(prevPos.x, prevPos.y, newScale))
        return newScale
      })
    },
    [scaleStep, minScale, getClampedPosition]
  )

  const resetControlsTimeout = useCallback(() => {
    if (controlsTimeout.current) {
      clearTimeout(controlsTimeout.current)
    }

    controlsTimeout.current = setTimeout(() => {
      if (!isDragging) {
        setShowControls(false)
      }
    }, 3000)
  }, [isDragging])

  const handleMouseDown = useCallback(
    e => {
      if (canDrag()) {
        e.preventDefault()
        setIsDragging(true)
        setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y })
        setShowControls(true)
      }
    },
    [canDrag, position.x, position.y]
  )

  const handleMouseMove = useCallback(
    e => {
      setShowControls(true)
      resetControlsTimeout()

      if (isDragging && canDrag()) {
        const newX = e.clientX - dragStart.x
        const newY = e.clientY - dragStart.y
        setPosition(getClampedPosition(newX, newY, scale))
      }
    },
    [isDragging, canDrag, dragStart.x, dragStart.y, scale, getClampedPosition, resetControlsTimeout]
  )

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false)
      resetControlsTimeout()
    }
  }, [isDragging, resetControlsTimeout])

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      setIsDragging(false)
      resetControlsTimeout()
    }
  }, [isDragging, resetControlsTimeout])

  const handleWheel = useCallback(
    e => {
      if (!open) return
      e.preventDefault()
      e.stopPropagation()
      setShowControls(true)

      const scaleAmount = e.deltaY < 0 ? scaleStep / 2 : -scaleStep / 2
      setScale(prevScale => {
        const newUnboundedScale = prevScale + scaleAmount * prevScale
        const newScale = Math.max(minScale, Math.min(maxScale, newUnboundedScale))

        if (containerRef && containerRef.current && imageDimensions.width > 0) {
          const rect = containerRef.current.getBoundingClientRect()
          const mouseX = e.clientX - rect.left
          const mouseY = e.clientY - rect.top

          const imageMouseX = (mouseX - position.x) / prevScale
          const imageMouseY = (mouseY - position.y) / prevScale

          const newPosX = mouseX - imageMouseX * newScale
          const newPosY = mouseY - imageMouseY * newScale

          setPosition(getClampedPosition(newPosX, newPosY, newScale))
        } else {
          setPosition(prevPos => getClampedPosition(prevPos.x, prevPos.y, newScale))
        }
        return newScale
      })
      resetControlsTimeout()
    },
    [
      open,
      scaleStep,
      minScale,
      maxScale,
      getClampedPosition,
      position.x,
      position.y,
      containerRef,
      imageDimensions.width,
      resetControlsTimeout
    ]
  )

  const getDistanceBetweenTouches = useCallback((touch1, touch2) => {
    const dx = touch1.clientX - touch2.clientX
    const dy = touch1.clientY - touch2.clientY
    return Math.sqrt(dx * dx + dy * dy)
  }, [])

  const handleTouchStart = useCallback(
    e => {
      if (!open) return
      e.preventDefault()
      setShowControls(true)

      if (e.touches.length === 1 && canDrag()) {
        setIsDragging(true)
        setDragStart({
          x: e.touches[0].clientX - position.x,
          y: e.touches[0].clientY - position.y
        })
      } else if (e.touches.length === 2) {
        setIsDragging(false)
        setPinchStartDist(getDistanceBetweenTouches(e.touches[0], e.touches[1]))
      }
    },
    [open, canDrag, position.x, position.y, getDistanceBetweenTouches]
  )

  const handleTouchMove = useCallback(
    e => {
      if (!open) return
      e.preventDefault()
      resetControlsTimeout()

      if (e.touches.length === 1 && isDragging && canDrag()) {
        const newX = e.touches[0].clientX - dragStart.x
        const newY = e.touches[0].clientY - dragStart.y
        setPosition(getClampedPosition(newX, newY, scale))
      } else if (e.touches.length === 2 && pinchStartDist > 0) {
        const currentDist = getDistanceBetweenTouches(e.touches[0], e.touches[1])
        const scaleChange = currentDist / pinchStartDist

        setScale(prevScale => {
          const newUnboundedScale = prevScale * scaleChange
          const newScale = Math.max(minScale, Math.min(maxScale, newUnboundedScale))

          // TODO: Implement zoom towards pinch center
          setPosition(prevPos => getClampedPosition(prevPos.x, prevPos.y, newScale))
          return newScale
        })
        setPinchStartDist(currentDist)
      }
    },
    [
      open,
      isDragging,
      canDrag,
      dragStart,
      scale,
      pinchStartDist,
      getDistanceBetweenTouches,
      minScale,
      maxScale,
      getClampedPosition,
      resetControlsTimeout
    ]
  )

  const handleTouchEnd = useCallback(
    e => {
      if (!open) return
      if (isDragging) {
        setIsDragging(false)
      }
      if (e.touches.length < 2) {
        setPinchStartDist(0)
      }
      resetControlsTimeout()
    },
    [open, isDragging, resetControlsTimeout]
  )

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      document.body.style.touchAction = 'none'
      setShowControls(true)
      resetControlsTimeout()
    } else {
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }
    return () => {
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
      if (controlsTimeout.current) {
        clearTimeout(controlsTimeout.current)
      }
    }
  }, [open, resetControlsTimeout])

  useEffect(() => {
    const handleKeyDown = e => {
      if (!open) return
      setShowControls(true)

      let actionTaken = false
      switch (e.key) {
        case '+':
        case '=':
          if (scale < maxScale) {
            setScale(prev => Math.min(prev + scaleStep, maxScale))
            actionTaken = true
          }
          break
        case '-':
        case '_':
          if (scale > minScale) {
            setScale(prev => Math.max(prev - scaleStep, minScale))
            actionTaken = true
          }
          break
        case '0':
        case 'r':
          resetView()
          actionTaken = true
          break
        case 'Escape':
          handleClose()
          actionTaken = true
          break
        case 'ArrowUp':
          if (canDrag()) {
            setPosition(prev => getClampedPosition(prev.x, prev.y + 20, scale))
            actionTaken = true
          }
          break
        case 'ArrowDown':
          if (canDrag()) {
            setPosition(prev => getClampedPosition(prev.x, prev.y - 20, scale))
            actionTaken = true
          }
          break
        case 'ArrowLeft':
          if (canDrag()) {
            setPosition(prev => getClampedPosition(prev.x + 20, prev.y, scale))
            actionTaken = true
          }
          break
        case 'ArrowRight':
          if (canDrag()) {
            setPosition(prev => getClampedPosition(prev.x - 20, prev.y, scale))
            actionTaken = true
          }
          break
        default:
          return
      }

      if (actionTaken) {
        e.preventDefault()
        resetControlsTimeout()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    open,
    scale,
    minScale,
    maxScale,
    scaleStep,
    resetView,
    handleClose,
    canDrag,
    getClampedPosition,
    resetControlsTimeout
  ])

  return {
    open,
    scale,
    minScale,
    maxScale,
    position,
    isDragging,
    showControls,

    initialMinScale,
    initialMaxScale,
    scaleStep,

    handleOpen,
    handleClose,
    resetView,
    handleZoomIn,
    handleZoomOut,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleWheel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    currentFitScale: currentFitScale.current
  }
}
